import { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Outlet, Navigate, useParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { AuthProvider } from "@/api/auth";
import ProtectedRoute from "@/components/ProtectedRoute";
import RequiredRole from "@/components/RequiredRole";
import AdminConsole from "@/pages/AdminConsole";
import CadLink from "@/pages/CadLink";

// Domain detection: designai.fofus.in → 3D generator, designers.fofus.in → marketplace,
// account.fofus.in → FOFUS Account (one ID, all services). Shared map: lib/sites.ts.
const isAccountSite =
  typeof window !== "undefined" && window.location.hostname.startsWith("account.");
const isDesignersSite =
  typeof window !== "undefined" && window.location.hostname.startsWith("designers.");

// --- Lazy-loaded route components ---
const Home = lazy(() => import("@/pages/Home"));
const Designers = lazy(() => import("@/pages/Designers"));
const DesignerProfile = lazy(() => import("@/pages/DesignerProfile"));
const DesignerMatching = lazy(() => import("@/pages/DesignerMatching"));
const StartProject = lazy(() => import("@/pages/StartProject"));
const Projects = lazy(() => import("@/pages/Projects"));
const ProjectWorkspace = lazy(() => import("@/pages/ProjectWorkspace"));
const CustomerDashboard = lazy(() => import("@/pages/CustomerDashboard"));
const MakerPage = lazy(() => import("@/pages/MakerPage"));
const CertifiedModel = lazy(() => import("@/pages/CertifiedModel"));
const PublicProjectPage = lazy(() => import("@/pages/PublicProjectPage"));
const ChallengesPage = lazy(() => import("@/pages/ChallengesPage"));
const ChallengeDetailPage = lazy(() => import("@/pages/ChallengeDetailPage"));
const StudentsPage = lazy(() => import("@/pages/StudentsPage"));
const LeaderboardPage = lazy(() => import("@/pages/LeaderboardPage"));
const CustomerProfile = lazy(() => import("@/pages/CustomerProfile"));
const DesignerDashboard = lazy(() => import("@/pages/DesignerDashboard"));
const ExecPage = lazy(() => import("@/pages/ExecPage"));
const DesignerOpportunities = lazy(() => import("@/pages/DesignerOpportunities"));
const DesignerEarnings = lazy(() => import("@/pages/DesignerEarnings"));
// Admin pages are lazy-imported inside AdminConsole's TABS registry (single shell, 09-21).
const Login = lazy(() => import("@/pages/Login"));
const Register = lazy(() => import("@/pages/Register"));
const Create3D = lazy(() => import("@/pages/Create3D"));
const FofusCAD = lazy(() => import("@/pages/FofusCAD"));
const Referral = lazy(() => import("@/pages/Referral"));
const Affiliate = lazy(() => import("@/pages/Affiliate"));
const BuyCredits = lazy(() => import("@/pages/BuyCredits"));
const HomeOld = lazy(() => import("@/pages/HomeOld"));
const Playground = lazy(() => import("@/pages/Playground"));
const Templates = lazy(() => import("@/pages/Templates"));
const SearchPage = lazy(() => import("@/pages/SearchPage"));
const CollectionsPage = lazy(() => import("@/pages/CollectionsPage"));
const ExplorePage = lazy(() => import("@/pages/ExplorePage"));
const ManufacturePage = lazy(() => import("@/pages/ManufacturePage"));
const OrdersPage = lazy(() => import("@/pages/OrdersPage"));
const MakerDashboardPage = lazy(() => import("@/pages/MakerDashboardPage"));
const FofusBridgePage = lazy(() => import("@/pages/FofusBridgePage"));
const ModelStore = lazy(() => import("@/pages/ModelStore"));
const ModelStoreDetail = lazy(() => import("@/pages/ModelStoreDetail"));
const DesignerModelUpload = lazy(() => import("@/pages/DesignerModelUpload"));
const AccountPage = lazy(() => import("@/pages/AccountPage"));
const PublicProfile = lazy(() => import("@/pages/PublicProfile"));

function PageFallback() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-ink-300 border-t-primary-600" />
    </div>
  );
}

// Admin console catch-all (28 Oct): /admin/<seg> renders the ONE console —
// known segment (users, groups, …) → its tab; unknown segment (e.g.
// /admin/reventer) → console landing. Keeps every legacy/deep admin link
// inside the single shell instead of a blank page.
function AdminConsoleCatchAll() {
  const { tab } = useParams();
  const to = tab ? `/admin?tab=${encodeURIComponent(tab)}` : "/admin";
  return <Navigate to={to} replace />;
}

// ── Role-protected wrapper ─────────────────────────────
// SSORoute: FOFUS ID role gate (W2 P1 / EXEC DIRECTIVE 08-31 #1).
// Silent-SSO aware: a valid foid_session cookie on .fofus.in satisfies the
// gate without a manual login. roles=["designer","admin"] on designer routes;
// admin is never hidden — every gate includes "admin" per exec law.
function SSORoute({ roles, site, children }: { roles: string[]; site?: string; children: React.ReactNode }) {
  return (
    <RequiredRole roles={roles} site={site}>
      {children}
    </RequiredRole>
  );
}

// Admin-inclusive designer gate: designer tools show for designer|admin.
const DESIGNER_ROLES = ["designer", "admin"];

// ── Designer-host gate (Amendment K, 09-21) ─────────────
// Designer surfaces live ONLY on designers.fofus.in. On any other host
// (designai.fofus.in etc.) a client-side navigation to a /designer/* path
// hard-redirects to the same path on the designers.* surface — a full
// document load, so the server-level host redirect completes the hop.
// designai itself only ever shows designai (customer) features.
function DesignerHostGate() {
  if (!isDesignersSite) {
    if (typeof window !== "undefined") {
      window.location.replace(
        `https://designers.fofus.in${window.location.pathname}${window.location.search}`
      );
    }
    return null;
  }
  return <Outlet />;
}

// ── Account-host gate (mirror of DesignerHostGate) ─────────────
// Account surfaces live ONLY on account.fofus.in. On any other host a client
// navigation to an /account/* path redirects to account.fofus.in (full
// document load, so the foid_session SSO cookie rides along to the new host).
function AccountHostGate() {
  if (!isAccountSite) {
    if (typeof window !== "undefined") {
      window.location.replace(
        `https://account.fofus.in${window.location.pathname}${window.location.search}`
      );
    }
    return null;
  }
  return <Outlet />;
}

export default function App() {
  return (
    <BrowserRouter>
      {/* AI bubble REMOVED per user law (09-04): "ai should be talking in
          cards — for each work AI data will be different." designers.fofus.in
          has no floating AI chat; AI guidance lives inside the work cards. */}
      <AuthProvider>
        <Suspense fallback={<PageFallback />}>
          <Routes>
            <Route element={<Layout />}>
              {/* ── Public routes ─────────────────────────── */}
              {/* founder 28 Oct (rev 2): the PLAYGROUND lives at /create-3d —
                  the surface quote.fofus's bot and the "No CAD?" handoff
                  already link (create-3d?ref=quote&desc=…). / returns a
                  redirect to it; HomeOld preserved at /classic (rollback:
                  point / and /create-3d back at HomeOld, 2 lines). */}
              <Route path="/" element={isAccountSite ? <AccountPage /> : isDesignersSite ? <Home /> : <Navigate to="/create-3d" replace />} />
              <Route path="/create-3d" element={isAccountSite ? <AccountPage /> : <Playground />} />
              <Route path="/create-3d-classic" element={<Create3D />} />
              <Route path="/classic" element={isAccountSite ? <AccountPage /> : <HomeOld />} />
              {/* /home + /studio fold into the surfaces above; SPA links redirect */}
              <Route path="/home" element={<Navigate to="/" replace />} />
              <Route path="/studio" element={<Navigate to="/" replace />} />
              <Route path="/marketplace" element={<Home />} />
              {/* 28 Oct: /create-3d = Playground (defined above); the OLD route
                  here shadowed it (last-match wins) and its auth wall auto-popped
                  the FOFUS ID gate on cold visits — removed. */}
              <Route path="/cad" element={<FofusCAD />} />
              <Route path="/cad-link" element={<CadLink />} />
              <Route path="/designers" element={<Designers />} />
              <Route path="/designers/:id" element={<DesignerProfile />} />
              <Route path="/designer-matching" element={<DesignerMatching />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/profile/:handle" element={
                <Suspense fallback={null}><PublicProfile /></Suspense>
              } />
              {/* Personal tracking links (17 Oct): /r/<code> lands straight in the
                  signup funnel with the code prefilled. Click-row recording is
                  server-side (307 from /api/track/click) — for browsers WITHOUT
                  JS, nginx rewrite, or prefetchers that follow redirects. */}
              <Route path="/r/:code" element={<Navigate to="/register" replace />} />
              {/* Secure individual links (17 Oct): browser-visits to /k/<payload>
                  land here; the API (/api/track/k/...) sets the binding cookie via
                  its own redirect, so this SPA route only catches direct visits. */}
              <Route path="/k/:payload" element={<Navigate to="/" replace />} />
              {/* founder 16 Oct (merged): EXPLORE = one discovery surface — the model
                  library (FindModels/OnlineModels) lives INSIDE Explore as a tab;
                  /online-models + /find-models redirect to /explore. */}
              <Route path="/online-models" element={<Navigate to="/explore" replace />} />
              <Route path="/find-models" element={<Navigate to="/explore" replace />} />
              <Route path="/templates" element={<Templates />} />
              {/* 14 Oct maker-surface brand: /makers is the SEO hub, /makerlab is
              a friendly alias for makerlab-style typing traffic. Same page. */}
              <Route path="/makers" element={<Templates />} />
              <Route path="/makerlab" element={<Templates />} />
              <Route path="/quick-design" element={<Templates />} />
              <Route path="/search" element={<SearchPage />} />
              <Route path="/collections" element={<CollectionsPage />} />
              <Route path="/explore" element={<ExplorePage />} />
              <Route path="/models" element={<ModelStore />} />
              <Route path="/models/:id" element={<ModelStoreDetail />} />
              {/* Public showcase — Student Flywheel Phase A (09-19) */}
              <Route path="/maker/:code" element={<MakerPage />} />
              {/* 16 Oct: print-validity certification — the print-validated wedge (GO; study research/3d-model-generator-business-2026-10-15.md) */}
              <Route path="/certified" element={<CertifiedModel />} />
              <Route path="/project/:num" element={<PublicProjectPage />} />
              {/* Challenges — Student Flywheel Phase B (09-20) */}
              <Route path="/challenges" element={<ChallengesPage />} />
              <Route path="/challenges/:id" element={<ChallengeDetailPage />} />

              {/* Funnel — Student Flywheel Phase C/D (09-20) */}
              <Route path="/students" element={<StudentsPage />} />
              {/* Earn loop — Student Flywheel Phase E (09-20) */}
              <Route path="/leaderboard" element={<LeaderboardPage />} />

              {/* ── Authenticated routes (any logged-in user) ── */}
              <Route path="/scan/start" element={
                <ProtectedRoute><StartProject /></ProtectedRoute>
              } />
              <Route path="/projects" element={
                <ProtectedRoute><Projects /></ProtectedRoute>
              } />
              <Route path="/projects/:id" element={
                <ProtectedRoute><ProjectWorkspace /></ProtectedRoute>
              } />
              <Route path="/customer/dashboard" element={
                <ProtectedRoute><CustomerDashboard /></ProtectedRoute>
              } />
              <Route path="/customer/profile" element={
                <ProtectedRoute><CustomerProfile /></ProtectedRoute>
              } />
              <Route path="/referral" element={
                <ProtectedRoute><Referral /></ProtectedRoute>
              } />
              <Route path="/affiliate" element={
                <ProtectedRoute><Affiliate /></ProtectedRoute>
              } />
              <Route path="/credits" element={
                <ProtectedRoute><BuyCredits /></ProtectedRoute>
              } />
              <Route path="/orders" element={
                <ProtectedRoute><OrdersPage /></ProtectedRoute>
              } />
              <Route path="/manufacture" element={
                <ProtectedRoute><ManufacturePage /></ProtectedRoute>
              } />
              <Route path="/maker-dashboard" element={
                <ProtectedRoute><MakerDashboardPage /></ProtectedRoute>
              } />
              <Route path="/maker-dashboard/fofus-bridge" element={
                <ProtectedRoute><FofusBridgePage /></ProtectedRoute>
              } />

              {/* ── Designer-only routes (FOFUS ID role gate, silent-SSO aware).
                  Amendment K (09-21): these render ONLY on designers.fofus.in —
                  DesignerHostGate hard-redirects any other host to designers.*,
                  so designai never shows designer surfaces in-SPA. ── */}
              <Route element={<DesignerHostGate />}>
                <Route path="/designer/board" element={
                  <SSORoute roles={DESIGNER_ROLES} site="the designer workspace"><DesignerDashboard /></SSORoute>
                } />
                <Route path="/designer/models/new" element={
                  <SSORoute roles={DESIGNER_ROLES} site="the model upload page"><DesignerModelUpload /></SSORoute>
                } />
                <Route path="/designer/dashboard" element={
                  <SSORoute roles={DESIGNER_ROLES} site="the designer workspace"><DesignerDashboard /></SSORoute>
                } />
                <Route path="/designer/opportunities" element={
                  <SSORoute roles={DESIGNER_ROLES} site="designer opportunities"><DesignerOpportunities /></SSORoute>
                } />
                <Route path="/designer/earnings" element={
                  <SSORoute roles={DESIGNER_ROLES} site="designer earnings"><DesignerEarnings /></SSORoute>
                } />
              </Route>

              {/* ── FOFUS Account routes (account.fofus.in, 02 Oct) ──────
                  Host-gated: these render ONLY on account.fofus.in. On any
                  other host a client navigation hard-redirects there (full
                  document load) so the foid_session SSO cookie rides along.
                  Auth = the shared FOFUS ID (JWT or silent-SSO cookie). */}
              <Route element={<AccountHostGate />}>
                <Route
                  path="/account"
                  element={
                    <ProtectedRoute>
                      <AccountPage />
                    </ProtectedRoute>
                  }
                />
                {/* B3: exec account lane — OWN credential class (exec: JWT), NOT a FOFUS-ID
                    session. No ProtectedRoute: the page itself gates (sessionStorage JWT +
                    backend 403 on wrong identity). Customer data never renders here. */}
                <Route
                  path="/exec"
                  element={<ExecPage />}
                />
              </Route>

              {/* ── Admin-only routes (role gate; admin TOTP enforced server-side) ──
                  All 11 admin surfaces render ONE AdminConsole shell (09-21); the
                  path pre-selects the tab, ?tab= keeps deep links working. */}
              <Route path="/admin" element={
                <SSORoute roles={["admin"]} site="the admin console"><AdminConsole initialTab="overview" /></SSORoute>
              } />
              <Route path="/admin/users" element={
                <SSORoute roles={["admin"]} site="user administration"><AdminConsole initialTab="users" /></SSORoute>
              } />
              <Route path="/admin/customers" element={
                <SSORoute roles={["admin"]} site="the customer ledger"><AdminConsole initialTab="customers" /></SSORoute>
              } />
              <Route path="/admin/designers" element={
                <SSORoute roles={["admin"]} site="designer administration"><AdminConsole initialTab="designers" /></SSORoute>
              } />
              <Route path="/admin/tryouts" element={
                <SSORoute roles={["admin"]} site="tryout grading"><AdminConsole initialTab="tryouts" /></SSORoute>
              } />
              <Route path="/admin/projects" element={
                <SSORoute roles={["admin"]} site="project administration"><AdminConsole initialTab="projects" /></SSORoute>
              } />
              <Route path="/admin/payments" element={
                <SSORoute roles={["admin"]} site="payment administration"><AdminConsole initialTab="payments" /></SSORoute>
              } />
              <Route path="/admin/disputes" element={
                <SSORoute roles={["admin"]} site="dispute resolution"><AdminConsole initialTab="disputes" /></SSORoute>
              } />
              <Route path="/admin/analytics" element={
                <SSORoute roles={["admin"]} site="platform analytics"><AdminConsole initialTab="analytics" /></SSORoute>
              } />
              <Route path="/admin/model-store" element={
                <SSORoute roles={["admin"]} site="the model review queue"><AdminConsole initialTab="model-store" /></SSORoute>
              } />
              <Route path="/admin/coins" element={
                <SSORoute roles={["admin"]} site="coin economy administration"><AdminConsole initialTab="coins" /></SSORoute>
              } />
              <Route path="/admin/iteration" element={
                <SSORoute roles={["admin"]} site="iteration analytics"><AdminConsole initialTab="iteration" /></SSORoute>
              } />
              <Route path="/admin/blog" element={
                <SSORoute roles={["admin"]} site="blog posting"><AdminConsole initialTab="blog" /></SSORoute>
              } />
              <Route path="/admin/groups" element={
                <SSORoute roles={["admin"]} site="group administration"><AdminConsole initialTab="groups" /></SSORoute>
              } />
              {/* Admin catch-all (28 Oct): every /admin/<anything> path renders
                  the ONE console (path segment carried in ?tab= when it matches
                  a tab key, e.g. legacy links); unknown segments land on the
                  console Dashboard instead of a blank page */}
              <Route path="/admin/:tab" element={<AdminConsoleCatchAll />} />
              <Route path="/admin/*" element={<AdminConsoleCatchAll />} />
            </Route>
          </Routes>
        </Suspense>
      </AuthProvider>
    </BrowserRouter>
  );
}